/**
 * Inspector multi-editing (Unity's multi-object editing): values shared by
 * every selected entity are shown, values that differ are "mixed" (—), and
 * an edit writes only the changed field (or vector component) to each
 * entity, as one batch command (one undo step).
 */

import { fullTransform, type EditorCommand, type EntityDesc, type Vec3 } from '../../lib/scene-doc.js';

/** Marks a value that differs across the selection. */
export const MIXED = Symbol('mixed');

export function isMixed(value: unknown): value is typeof MIXED {
  return value === MIXED;
}

function equal(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

/**
 * One value across entities: the value when all are equal; numeric arrays of
 * the same length merge per element (differing elements are {@link MIXED});
 * anything else that differs is {@link MIXED}.
 */
export function mergeValues(values: readonly unknown[]): unknown {
  if (values.length === 0) return undefined;
  const first = values[0];
  if (values.every((v) => equal(v, first))) return first;
  const numericArrays = values.every((v) => Array.isArray(v) && v.every((n) => typeof n === 'number'));
  if (numericArrays && values.every((v) => (v as number[]).length === (first as number[]).length)) {
    return (first as number[]).map((_, i) => {
      const at = values.map((v) => (v as number[])[i]);
      return at.every((n) => n === at[0]) ? at[0] : MIXED;
    });
  }
  return MIXED;
}

/** Per-key merge of component records (a key some lack is mixed). */
export function mergeRecords(records: readonly Record<string, unknown>[]): Record<string, unknown> {
  const keys = new Set(records.flatMap((r) => Object.keys(r)));
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const present = records.filter((r) => k in r);
    out[k] = present.length === records.length ? mergeValues(records.map((r) => r[k])) : MIXED;
  }
  return out;
}

/** Which elements of a merged vector are mixed (`undefined` = none / not a vector). */
export function mixedMask(value: unknown): boolean[] | undefined {
  if (isMixed(value)) return [true, true, true, true];
  if (!Array.isArray(value) || !value.some(isMixed)) return undefined;
  return value.map(isMixed);
}

/** A merged vector with mixed elements as 0 (their editors show "—"). */
export function vectorOf(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.map((v) => (typeof v === 'number' ? v : 0));
}

/** Component keys every entity has, in the first entity's order. */
export function commonComponents(entities: readonly EntityDesc[]): string[] {
  const [first, ...rest] = entities;
  if (!first) return [];
  return Object.keys(first.components ?? {}).filter((k) => rest.every((e) => k in (e.components ?? {})));
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function withElement(own: unknown, next: unknown, index: number | undefined): unknown {
  if (index === undefined || !Array.isArray(next)) return next;
  const base = Array.isArray(own) ? own.slice() : next.slice();
  base[index] = next[index];
  return base;
}

/**
 * Set field `field` of component `key` on every entity (`undefined` removes
 * it). With `index`, only that element of a vector field changes.
 */
export function setFieldCommands(
  entities: readonly EntityDesc[],
  key: string,
  field: string,
  value: unknown,
  index?: number,
  clears: readonly string[] = [],
): EditorCommand[] {
  return entities.map((e) => {
    const own = record(e.components?.[key]);
    const next: Record<string, unknown> = { ...own };
    const v = withElement(own[field], value, index);
    if (v === undefined) delete next[field];
    else next[field] = v;
    for (const c of clears) delete next[c];
    return { cmd: 'setComponent', id: e.id, key, value: next };
  });
}

/** Set a whole component value on every entity (scalar components, JSON, reset). */
export function setComponentCommands(entities: readonly EntityDesc[], key: string, value: unknown): EditorCommand[] {
  return entities.map((e) => ({ cmd: 'setComponent', id: e.id, key, value }));
}

/** Add component `key` with `value` to the entities that lack it. */
export function addComponentCommands(entities: readonly EntityDesc[], key: string, value: unknown): EditorCommand[] {
  return entities.filter((e) => !(key in (e.components ?? {}))).map((e) => ({ cmd: 'setComponent', id: e.id, key, value }));
}

export function removeComponentCommands(entities: readonly EntityDesc[], key: string): EditorCommand[] {
  return entities.filter((e) => key in (e.components ?? {})).map((e) => ({ cmd: 'removeComponent', id: e.id, key }));
}

/** Set transform `prop` (or one of its axes) on every entity. */
export function transformCommands(
  entities: readonly EntityDesc[],
  prop: 'position' | 'rotation' | 'scale',
  value: readonly number[],
  index?: number,
): EditorCommand[] {
  return entities.map((e) => {
    const t = fullTransform(e.transform);
    const next = (index === undefined ? value.slice() : t[prop].map((v, i) => (i === index ? value[i]! : v))) as Vec3;
    return { cmd: 'setTransform', id: e.id, transform: { ...t, [prop]: next } };
  });
}

/** Merged position / rotation / scale of the entities. */
export function mergedTransform(entities: readonly EntityDesc[]): Record<'position' | 'rotation' | 'scale', unknown> {
  const ts = entities.map((e) => fullTransform(e.transform));
  return {
    position: mergeValues(ts.map((t) => t.position)),
    rotation: mergeValues(ts.map((t) => t.rotation)),
    scale: mergeValues(ts.map((t) => t.scale)),
  };
}
